import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MarkdownContent from '../shared/MarkdownContent';
import Navbar from '../shared/Navbar';
import { api } from '../../services/api';
import { DetailedLesson } from '../../types';

const AdminLessonPreviewPage: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = React.useState<DetailedLesson | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [me, setMe] = React.useState<{ authenticated: boolean; user?: { email: string; is_admin: boolean } } | null>(null);

  React.useEffect(() => {
    const loadLesson = async () => {
      if (!lessonId) return;
      setLoading(true);
      setError(null);
      try {
        try {
          const current = await api.me();
          setMe(current);
        } catch {
          setMe({ authenticated: false });
        }
        const data = await api.getLesson(lessonId);
        setLesson(data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };
    void loadLesson();
  }, [lessonId]);

  if (!lessonId) {
    return <div>Missing lesson id.</div>;
  }

  return (
    <div className="admin-lesson-preview">
      <Navbar />
      <main className="app-main">
        <div className="content-container">
          <div className="page-intro">
            <h2>Lesson Preview</h2>
            <p>Full content view for admins</p>
          </div>
          {!(me?.authenticated && me?.user?.is_admin) && (
            <div className="admin-guard">
              <div className="admin-guard-title">Admin access required</div>
              <div className="admin-guard-body">You must be signed in as an admin to view lesson previews.</div>
              <div className="admin-guard-actions">
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/login">Sign In</Link>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/admin/lessons">Back to Manage Lessons</Link>
              </div>
            </div>
          )}
          {!(me?.authenticated && me?.user?.is_admin) ? null : (
            <div className="tm-surface admin-lesson-preview-card">
              <div className="preview-toolbar">
                <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => navigate(-1)}>← Back</button>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/admin/lessons">Manage Lessons</Link>
              </div>
              {loading && <div className="ml-subtle">Loading lesson…</div>}
              {error && <div className="ml-error">{error}</div>}
              {!loading && !error && lesson && (
                <>
                  <div className="preview-header">
                    <h2>{lesson.title}</h2>
                    <p>{lesson.description}</p>
                  </div>
                  <div className="preview-sections">
                    {lesson.sections.map((section, index) => (
                      <div key={`${lesson.id}-${index}`} className="preview-section">
                        <div className="preview-section-title">
                          <span className="preview-section-index">Section {index + 1}</span>
                          <h3>{section.name}</h3>
                        </div>
                        <p className="preview-section-desc">{section.description}</p>
                        <div className="preview-section-block">
                          <div className="preview-section-label">First Message</div>
                          <div className="preview-markdown">
                            <MarkdownContent content={section.first_message || ''} />
                          </div>
                        </div>
                        <div className="preview-section-block">
                          <div className="preview-section-label">Content</div>
                          <div className="preview-markdown">
                            <MarkdownContent content={section.content || ''} />
                          </div>
                        </div>
                        {section.flashcards && section.flashcards.length > 0 && (
                          <div className="preview-section-block">
                            <div className="preview-section-label">Flashcards</div>
                            <div className="preview-markdown">
                              {section.flashcards.map((card, cardIndex) => (
                                <div key={`${lesson.id}-${index}-card-${cardIndex}`} className="preview-flashcard">
                                  <div className="preview-flashcard-title">Card {cardIndex + 1}</div>
                                  <div className="preview-flashcard-face">
                                    <div className="preview-flashcard-label">Top Text</div>
                                    <MarkdownContent content={card.top_text || ''} />
                                  </div>
                                  <div className="preview-flashcard-face">
                                    <div className="preview-flashcard-label">Bottom Text</div>
                                    <MarkdownContent content={card.bottom_text || ''} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminLessonPreviewPage;
